import {
  MAX_NAME_BYTES,
  MAX_SYMBOL_BYTES,
  MAX_SUPPLY_SMALLEST_UNITS,
  MAX_SUPPLY_WHOLE_TOKENS,
  TOKEN_DECIMALS,
  byteLength,
} from "../config/limits";

export interface FieldResult {
  ok: boolean;
  error?: string;
}

/** Name: 1..MAX_NAME_BYTES UTF-8 bytes, not whitespace-only. */
export function validateName(name: string): FieldResult {
  if (name.trim().length === 0) return { ok: false, error: "Enter a token name." };
  const bytes = byteLength(name);
  if (bytes > MAX_NAME_BYTES) {
    return { ok: false, error: `Name is ${bytes} bytes; the limit is ${MAX_NAME_BYTES} bytes.` };
  }
  return { ok: true };
}

/** Symbol: 1..MAX_SYMBOL_BYTES UTF-8 bytes, not whitespace-only. */
export function validateSymbol(symbol: string): FieldResult {
  if (symbol.trim().length === 0) return { ok: false, error: "Enter a token symbol." };
  const bytes = byteLength(symbol);
  if (bytes > MAX_SYMBOL_BYTES) {
    return { ok: false, error: `Symbol is ${bytes} bytes; the limit is ${MAX_SYMBOL_BYTES} bytes.` };
  }
  return { ok: true };
}

/** Uppercase a symbol only when it is plain ASCII (safe transform). */
export function normalizeSymbol(symbol: string): string {
  const isAscii = [...symbol].every((c) => c.charCodeAt(0) <= 0x7f);
  return isAscii ? symbol.toUpperCase() : symbol;
}

export interface SupplyResult extends FieldResult {
  whole?: bigint;
  smallestUnits?: bigint;
}

/**
 * Parse a WHOLE-token supply. bigint only. Rejects empty, decimals, scientific
 * notation, separators, negatives, and zero, and enforces the canonical maximum
 * (guarding the 10^18 multiply against overflow of the on-chain cap).
 */
export function parseSupply(input: string): SupplyResult {
  const raw = input.trim();
  if (raw.length === 0) return { ok: false, error: "Enter a supply." };
  if (/[eE]/.test(raw)) return { ok: false, error: "Scientific notation is not allowed." };
  if (raw.includes(".")) return { ok: false, error: "Whole tokens only — no decimals." };
  if (raw.startsWith("-")) return { ok: false, error: "Supply must be positive." };
  if (/[,_\s]/.test(raw)) return { ok: false, error: "Remove separators; digits only." };
  if (!/^\d+$/.test(raw)) return { ok: false, error: "Digits only." };

  let whole: bigint;
  try {
    whole = BigInt(raw);
  } catch {
    return { ok: false, error: "Invalid number." };
  }
  if (whole <= 0n) return { ok: false, error: "Supply must be greater than zero." };
  if (whole > MAX_SUPPLY_WHOLE_TOKENS) {
    return {
      ok: false,
      error: `Supply exceeds the maximum of ${MAX_SUPPLY_WHOLE_TOKENS.toString()} whole tokens.`,
    };
  }
  const smallestUnits = whole * 10n ** TOKEN_DECIMALS;
  // Defensive: equivalent to the whole-token check, but explicit against the cap.
  if (smallestUnits > MAX_SUPPLY_SMALLEST_UNITS) {
    return { ok: false, error: "Supply exceeds the on-chain maximum." };
  }
  return { ok: true, whole, smallestUnits };
}

/** Accept only http(s) URLs; used for optional website/social links. */
export function validateUrl(url: string): FieldResult {
  const raw = url.trim();
  if (raw.length === 0) return { ok: true }; // optional
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return { ok: false, error: "Enter a valid URL (including https://)." };
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return { ok: false, error: "Only http(s) links are allowed." };
  }
  return { ok: true };
}
